import { Card, CardContent } from "@repo/ui/card";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { fetchPropertyAssets, signStoredArtifact, type PropertyAsset } from "@/lib/api";

function Asset({ asset }: { asset: PropertyAsset }) {
  const [failed, setFailed] = useState(false);
  const signed = useQuery({
    queryKey: ["property-asset-url", asset.id],
    queryFn: () => signStoredArtifact(asset.storage_bucket, asset.storage_path),
    staleTime: 45 * 60 * 1000,
    retry: false,
  });
  const image = asset.asset_type === "photo" || asset.asset_type === "floorplan";
  return (
    <div className="rounded border overflow-hidden">
      {signed.data && image && !failed ? (
        <a href={signed.data} target="_blank" rel="noopener noreferrer">
          <img
            src={signed.data}
            alt={`${asset.asset_type} from property snapshot`}
            loading="lazy"
            className="h-36 w-full object-cover"
            onError={() => setFailed(true)}
          />
        </a>
      ) : (
        <div className="h-36 grid place-items-center bg-muted text-sm text-muted-foreground">
          {signed.isLoading
            ? "Loading…"
            : signed.error || failed
              ? "Preview unavailable"
              : asset.asset_type}
        </div>
      )}
      <div className="p-2 text-xs flex justify-between gap-2">
        <span className="capitalize">{asset.asset_type}</span>
        {signed.data ? (
          <a
            href={signed.data}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Open
          </a>
        ) : null}
      </div>
    </div>
  );
}

export function PropertyAssets({ snapshotIds }: { snapshotIds: string[] }) {
  const query = useQuery({
    queryKey: ["property-assets", snapshotIds],
    queryFn: () => fetchPropertyAssets(snapshotIds),
    enabled: snapshotIds.length > 0,
  });
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <h2 className="font-semibold">Stored artifacts ({query.data?.length ?? 0})</h2>
        {query.isLoading && <p className="text-sm text-muted-foreground">Loading artifacts…</p>}
        {query.error && (
          <p className="text-sm text-destructive">
            Could not load artifacts: {query.error.message}
          </p>
        )}
        {query.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No downloaded artifacts attached to these snapshots.
          </p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {query.data?.map((asset) => (
            <Asset key={asset.id} asset={asset} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
